<template>
    <div class="feedback-inbox">
        <div class="d-flex flex-wrap align-items-center mb-3">
            <b-form-select
                v-model="statusFilter"
                :options="statusFilterOptions"
                style="max-width: 180px"
                class="mr-2"
                aria-label="상태 필터"
                @change="onStatusChange"
            />
            <span class="mr-auto">
                <span class="text-muted"><template v-if="!loadError">총 {{ feedback.length }}건 / </template>선택</span>
                <span :class="selectionAtLimit ? 'text-warning font-weight-bold' : 'text-muted'">
                    {{ selectedIds.length }}/{{ MAX_SELECT }}건
                </span>
                <span v-if="selectionAtLimit" class="text-warning small ml-1">
                    (상한 도달 — 더 고르려면 먼저 반영하거나 선택을 해제하세요)
                </span>
            </span>

            <!-- ⛔ 반영 대상(판례/템플릿) 선택을 여기 두지 않는다 — 이 화면을 쓰는 사람은
                 ESG 평가자이고, 어디에 반영할지는 AI 가 정한다. -->
            <b-button
                variant="primary"
                class="mr-2"
                :disabled="!selectedIds.length || dismissing"
                @click="openPreview"
            >
                AI 판단 기준에 반영
            </b-button>
            <b-button
                variant="outline-secondary"
                class="mr-2"
                :disabled="!selectedIds.length || dismissing"
                @click="dismissSelected"
            >
                <b-spinner v-if="dismissing" small class="mr-1" />{{ dismissing ? `기각 중 ${dismissDone}/${dismissTotal}` : '기각' }}
            </b-button>
            <b-button variant="outline-secondary" @click="openManualModal">
                수동 피드백 추가
            </b-button>
        </div>

        <!-- 이어보기 — 페이지를 이탈했다 돌아와도 진행 중이던 개선 제안을 놓치지 않는다.
             상태는 서버(proposal)가 보관하므로 proposal id 만 있으면 그대로 이어본다.
             링크(?proposal=)로 받은 제안도 여기서 [이어보기] 를 눌러 연다 — 초안이 비어 있으면 모달을 열자마자
             AI 생성(비용)이 시작될 수 있어 주소만으로는 열지 않는다. -->
        <b-alert :show="!!bannerProposalId && !previewOpen" variant="info" class="py-2">
            <div class="d-flex align-items-center">
                <span v-if="urlProposalId" class="mr-auto">
                    링크로 받은 개선 제안(#{{ urlProposalId }})입니다. 이어서 확인할 수 있습니다.
                </span>
                <span v-else class="mr-auto">
                    이 브라우저에서 진행 중인 개선 제안(#{{ pendingProposalId }})이 있습니다.
                    이어서 확인할 수 있습니다.
                </span>
                <b-button size="sm" variant="primary" class="mr-2" @click="resumePreview">
                    이어보기
                </b-button>
                <b-button size="sm" variant="outline-secondary" @click="dismissPendingProposal">
                    지우기
                </b-button>
            </div>
        </b-alert>

        <b-alert :show="truncated" variant="warning" class="py-2">
            서버가 한 상태당 {{ MAX_ROWS }}건까지만 내려줍니다. 최신 {{ MAX_ROWS }}건만 표시 중이며,
            반영하거나 기각해 처리한 만큼 이전 피드백이 다시 나타납니다.
        </b-alert>

        <!-- 한 페이지 = 한 번에 반영 가능한 최대 건수(20).
             DS 표 규칙: 행 44 고정 · 폭은 머리가 정하고(내용 칸이 남는 폭) 넘치면 줄인다.
             내용 전문 · 대상별 진행은 펼침(row-details), 행 액션은 맨 끝 한 칸. -->
        <b-table
            :items="feedback"
            :fields="fields"
            :busy="loading"
            :per-page="MAX_SELECT"
            :current-page="currentPage"
            table-class="ds-table ds-table-fixed feedback-inbox-table"
            hover
            responsive
            show-empty
        >
            <template #empty>
                <PageEmptyComponent
                    compact
                    :error="loadError ? '피드백 목록을 불러오지 못했습니다.' : ''"
                    text="표시할 피드백이 없습니다."
                    @retry="fetchFeedback"
                />
            </template>

            <template #head(selected)>
                <b-form-checkbox
                    :checked="allPageChecked"
                    :disabled="!pageSelectableIds.length"
                    @change="toggleAll"
                />
            </template>

            <template #cell(selected)="row">
                <b-form-checkbox
                    :checked="selectedIds.includes(row.item.id)"
                    :disabled="isCheckDisabled(row.item)"
                    @change="toggleOne(row.item.id)"
                />
            </template>

            <!-- 판정 흐름 — AI 가 뭐라 했고 평가자가 어떻게 했는지(한 줄, 넘치면 title). -->
            <template #cell(judgement)="row">
                <span
                    v-if="row.item.ai_status"
                    class="ds-ellipsis"
                    :title="`AI ${judgementLabel(row.item.ai_status)} → ${row.item.evaluator_status ? judgementLabel(row.item.evaluator_status) : '변경 없음'}`"
                ><span class="ds-sub">AI {{ judgementLabel(row.item.ai_status) }}</span> <span v-if="row.item.evaluator_status" class="ds-status ds-status-danger">→ {{ judgementLabel(row.item.evaluator_status) }}</span><span v-else class="ds-sub">→ 변경 없음</span></span>
                <span v-else class="ds-sub">-</span>
            </template>

            <template #cell(source_type)="row">
                <span class="ds-tag" :class="{'ds-tone-danger': sourceVariant(row.item.source_type) === 'danger'}">{{ sourceLabel(row.item.source_type) }}</span>
            </template>

            <!-- 문항 · 대상(캠페인 · 기업 ID)은 한 칸 — ID 는 흐린 글자로 뒤에 -->
            <template #cell(scope)="row">
                <span
                    class="ds-ellipsis"
                    :title="`문항 ${row.item.sub_code || '-'} · 캠페인 ${row.item.campaign_id || '-'} · 기업 ${row.item.company_id || '-'}`"
                >{{ row.item.sub_code || '-' }} <span class="ds-sub">캠페인 {{ row.item.campaign_id || '-' }} · 기업 {{ row.item.company_id || '-' }}</span></span>
            </template>

            <!-- 내용 = 펼침 링크(한 줄, 전문은 title · 펼침). 끝의 펼치기 아이콘과 같은 동작 -->
            <template #cell(content)="row">
                <button
                    type="button"
                    class="ds-detail-link ds-ellipsis"
                    :title="row.item.content"
                    :aria-expanded="row.detailsShowing ? 'true' : 'false'"
                    @click="row.toggleDetails()"
                >{{ row.item.content }}</button>
            </template>

            <!-- 반영 상태 — 서버 파생 feedback.status 를 세분화한 진행 상태 + 종결 n/m.
                 대상별 상태 · 적용 버전은 title 과 펼침에.
                 ⛔ 첫 초안 저장 후에도 피드백이 사라지지 않고 여기 부분진행으로 남는다. -->
            <template #cell(status)="row">
                <span class="ds-ellipsis" :title="progressTitle(row.item)"><span class="ds-status" :class="`ds-status-${progressVariant(row.item)}`">{{ progressLabel(row.item) }}</span> <span v-if="row.item.proposal && row.item.proposal.targets.length" class="ds-sub">{{ terminalCount(row.item.proposal) }}/{{ row.item.proposal.targets.length }}</span></span>
            </template>

            <template #cell(created_at)="row">
                {{ formatDateTime(row.item.created_at) }}
            </template>

            <!-- 행 액션(아이콘 28): 개선안 계속 보기 · 대기로 되돌리기(해당 행만 · 빈 자리 유지) · 펼치기 -->
            <template #cell(actions)="row">
                <div class="d-flex justify-content-end" style="gap: 4px">
                    <button
                        v-if="hasOpenProposal(row.item)"
                        type="button"
                        class="ds-row-icon-btn"
                        aria-label="개선안 계속 보기"
                        title="개선안 계속 보기"
                        @click="continueProposal(row.item)"
                    >
                        <AppIcon name="sparkles"/>
                    </button>
                    <span v-else class="ds-row-icon-btn" aria-hidden="true"></span>
                    <button
                        v-if="row.item.status === 'DISMISSED'"
                        type="button"
                        class="ds-row-icon-btn"
                        :disabled="restoringId === row.item.id"
                        aria-label="대기로 되돌리기"
                        title="대기로 되돌리기"
                        @click="restoreDismissed(row.item)"
                    >
                        <AppIcon name="arrow-back-up"/>
                    </button>
                    <span v-else class="ds-row-icon-btn" aria-hidden="true"></span>
                    <button
                        type="button"
                        class="ds-row-icon-btn"
                        :aria-expanded="row.detailsShowing ? 'true' : 'false'"
                        :aria-label="`내용 ${row.detailsShowing ? '접기' : '펼치기'}`"
                        :title="`내용 ${row.detailsShowing ? '접기' : '펼치기'}`"
                        @click="row.toggleDetails()"
                    >
                        <AppIcon :name="row.detailsShowing ? 'chevron-up' : 'chevron-down'"/>
                    </button>
                </div>
            </template>

            <!-- 펼침 — 행에서 줄인 내용 전문과 대상별 진행 · 적용 버전 -->
            <template #row-details="row">
                <div class="py-2">
                    <div class="feedback-content">{{ row.item.content }}</div>
                    <div
                        v-if="row.item.proposal && row.item.proposal.targets.length"
                        class="small text-muted mt-2"
                    >
                        <div>
                            진행 {{ terminalCount(row.item.proposal) }}/{{ row.item.proposal.targets.length }}
                        </div>
                        <div v-for="t in row.item.proposal.targets" :key="t.id">
                            {{ previewTargetLabel(t.asset_type) }}: {{ targetStatusLabel(t.status) }}
                        </div>
                    </div>
                    <div v-else-if="appliedList(row.item).length" class="small text-muted mt-2">
                        <span v-for="(a, i) in appliedList(row.item)" :key="i" class="mr-2">
                            {{ previewTargetLabel(a.asset_type) }} v{{ a.version }}
                        </span>
                    </div>
                </div>
            </template>
        </b-table>

        <div v-if="feedback.length > MAX_SELECT" class="d-flex align-items-center">
            <b-pagination
                :value="currentPage"
                :total-rows="feedback.length"
                :per-page="MAX_SELECT"
                size="sm"
                class="mb-0"
                @change="onPageChange"
            />
            <span class="ml-3 text-muted small">
                한 페이지 {{ MAX_SELECT }}건. 선택은 페이지를 넘겨도 유지되며,
                한 번에 {{ MAX_SELECT }}건까지 반영할 수 있습니다.
            </span>
        </div>

        <!-- 개선 제안 상세 — 신규(feedbackIds) / 이어보기(proposalId) / 계속(initialProposal). -->
        <ApplyPreviewModal
            v-if="previewOpen"
            :feedback-ids="selectedIds"
            :feedback-items="selectedItems"
            :initial-proposal="modalProposal"
            :proposal-id="modalProposalId"
            @proposal-created="onProposalCreated"
            @proposal-completed="onProposalCompleted"
            @close="onModalClose"
        />

        <!-- 수동 피드백 추가 — 실패해도 닫지 않고 입력을 남긴다. 등록 중에는 닫기 잠금. -->
        <b-modal
            id="manual-feedback-modal"
            title="수동 피드백 추가"
            ok-title="등록"
            cancel-title="취소"
            :busy="manualSubmitting"
            :no-close-on-backdrop="manualSubmitting"
            :no-close-on-esc="manualSubmitting"
            :hide-header-close="manualSubmitting"
            @ok="createManualFeedback"
        >
            <b-form-group label="피드백 내용" label-for="manual-feedback-content">
                <b-form-textarea
                    id="manual-feedback-content"
                    v-model="manualForm.content"
                    rows="5"
                    :state="manualSubmitted && !manualForm.content.trim() ? false : null"
                    placeholder="예: 인증서 만료일이 지난 첨부는 보완 필요로 판정해야 한다."
                />
                <b-form-invalid-feedback>피드백 내용을 입력해 주세요.</b-form-invalid-feedback>
            </b-form-group>
            <b-row>
                <b-col cols="4">
                    <b-form-group label="문항 코드" label-for="manual-feedback-sub">
                        <b-form-input id="manual-feedback-sub" v-model="manualForm.sub_code" placeholder="선택" />
                    </b-form-group>
                </b-col>
                <b-col cols="4">
                    <b-form-group label="캠페인 ID" label-for="manual-feedback-campaign">
                        <b-form-input
                            id="manual-feedback-campaign"
                            v-model="manualForm.campaign_id"
                            type="number"
                            placeholder="선택"
                        />
                    </b-form-group>
                </b-col>
                <b-col cols="4">
                    <b-form-group label="기업 ID" label-for="manual-feedback-company">
                        <b-form-input
                            id="manual-feedback-company"
                            v-model="manualForm.company_id"
                            type="number"
                            placeholder="선택"
                        />
                    </b-form-group>
                </b-col>
            </b-row>
            <b-alert :show="!!manualError" variant="danger" class="mb-0">{{ manualError }}</b-alert>
        </b-modal>

    </div>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';
import PageEmptyComponent from '@shared/components/admin/components/common/PageEmptyComponent.vue';
import ApplyPreviewModal from './ApplyPreviewModal.vue';
import {
    ACCEPTED_TARGET_TERMINALS,
    SOURCE_TYPE_VARIANTS,
    feedbackProgressLabel,
    feedbackProgressVariant,
    firstQueryValue,
    formatDateTime,
    judgementLabel,
    previewTargetLabel,
    queryPositiveInt,
    sameQuery,
    sourceTypeLabel,
    targetStatusLabel,
} from './prompt-labels';
import {clearActiveProposal, loadActiveProposal} from './preview-job-store';

// 백엔드 list_feedback 이 한 번에 내려주는 상한 (status 필터 적용 후 id desc 500건).
const MAX_ROWS = 500;

// 한 번에 반영할 수 있는 피드백 상한.
// ⛔ 백엔드 _MAX_PREVIEW_FEEDBACKS 와 같은 값이어야 한다.
const MAX_SELECT = 20;

// "작업함"이 모으는 서버 파생 원시 상태(아직 처리 중인 것들).
// 백엔드가 status 필터를 적용한 뒤 500건을 자르므로, status=ALL 로 받아 로컬 필터링하면
// 최근 APPLIED/DISMISSED 가 500건을 채웠을 때 오래된 미처리분을 놓친다 — 상태별로 각각 조회한다.
const WORKING_STATUSES = ['PENDING', 'IN_PROGRESS', 'READY'];

// 이 탭이 URL 에 남기는 키 — ?status=(작업함은 생략)&current_page=(1 은 생략)&proposal=(열린 개선 제안).
const QUERY_KEYS = ['status', 'current_page', 'proposal'];

export default {
    name: 'FeedbackInboxTab',
    components: {ApplyPreviewModal, PageEmptyComponent},
    data() {
        return {
            MAX_ROWS,
            MAX_SELECT,
            feedback: [],
            loading: false,
            loadError: false,
            fetchReq: 0,
            // 조회가 서버 500건 상한에 걸려 잘렸는지 — 경고 배너 표시용.
            truncated: false,
            statusFilter: 'OPEN',
            currentPage: 1,
            selectedIds: [],
            dismissing: false,
            dismissDone: 0,
            dismissTotal: 0,
            restoringId: null,
            previewOpen: false,
            // 모달 진입 파라미터 — 신규는 둘 다 null(selectedIds 사용), 이어보기/계속은 하나 지정.
            modalProposal: null,
            modalProposalId: null,
            // 이 브라우저에 남아 있는 진행 중 개선 제안 id — 이어보기 배너용.
            pendingProposalId: null,
            // URL ?proposal= — 링크로 받았거나 지금 모달로 연 개선 제안 id.
            urlProposalId: null,
            manualForm: {content: '', sub_code: '', campaign_id: '', company_id: ''},
            manualSubmitting: false,
            manualSubmitted: false,
            manualError: '',
            statusFilterOptions: [
                // ⛔ 영문 ENUM 을 라벨에 노출하지 않는다.
                {value: 'OPEN', text: '작업함'},
                {value: 'PENDING', text: '반영 대기'},
                {value: 'APPLIED', text: '반영됨'},
                {value: 'DISMISSED', text: '기각'},
                {value: 'ALL', text: '전체'},
            ],
            // 폭은 머리가 정한다(ds-table-fixed) — 판정은 'AI 평가자 확인 → 보완 불필요' 가 다 들어가게 고정,
            // 폭을 안 적은 문항 · 대상과 내용 칸이 남는 폭을 나눠 갖는다
            fields: [
                // 선택 칸(40)은 좌우 여백 8 — 공통 16 이면 체크박스가 가운데서 밀린다. 공통 규칙보다 앞서게 inline 으로
                {key: 'selected', label: '', thStyle: {width: '40px', paddingLeft: '8px', paddingRight: '8px'},
                    tdAttr: {style: 'padding-left: 8px; padding-right: 8px'},
                    thClass: 'text-center', tdClass: 'text-center'},
                {key: 'source_type', label: '출처', thStyle: {width: '116px'}},
                {key: 'scope', label: '문항 · 대상'},
                {key: 'judgement', label: '판정', thStyle: {width: '200px'}},
                {key: 'content', label: '내용'},
                {key: 'status', label: '반영', thStyle: {width: '128px'}},
                {key: 'created_at', label: '등록', tdClass: 'ds-cell-nowrap', thStyle: {width: '140px'}},
                {key: 'actions', label: '동작', thClass: 'text-right', tdClass: 'text-right', thStyle: {width: '124px'}},
            ],
        };
    },
    computed: {
        // 현재 페이지에 보이는 행 — 선택은 이 범위에만 작용한다.
        pageRows() {
            const start = (this.currentPage - 1) * MAX_SELECT;
            return this.feedback.slice(start, start + MAX_SELECT);
        },
        pageSelectableIds() {
            return this.pageRows.filter(this.isSelectable).map((f) => f.id);
        },
        allPageChecked() {
            return this.pageSelectableIds.length > 0
                && this.pageSelectableIds.every((id) => this.selectedIds.includes(id));
        },
        selectedItems() {
            return this.feedback.filter((f) => this.selectedIds.includes(f.id));
        },
        selectionAtLimit() {
            return this.selectedIds.length >= MAX_SELECT;
        },
        bannerProposalId() {
            return this.urlProposalId || this.pendingProposalId;
        },
    },
    watch: {
        // 뒤로가기·사이드바 재클릭으로 같은 화면의 URL 이 바뀌면 필터·페이지를 맞춘다.
        // 화면이 직접 바꾼 URL 은 이미 같은 상태라 다시 불러오지 않는다.
        '$route.query'(query) {
            this.applyQuery(query);
        },
    },
    created() {
        // 진행 중이던 개선 제안이 이 브라우저에 남아 있는지 확인한다.
        this.pendingProposalId = loadActiveProposal();
        this.applyQuery(this.$route.query, true);
    },
    beforeDestroy() {
        // 탭·화면을 벗어난 뒤 늦게 온 응답이 다른 화면의 URL 을 고치지 않게 진행 중 조회를 버린다.
        this.fetchReq += 1;
    },
    methods: {
        formatDateTime,
        judgementLabel,
        previewTargetLabel,
        targetStatusLabel,
        sourceLabel: sourceTypeLabel,
        progressLabel: feedbackProgressLabel,
        progressVariant: feedbackProgressVariant,
        applyQuery(query, force = false) {
            const status = firstQueryValue(query.status);
            const nextStatus = this.statusFilterOptions.some((o) => o.value === status) ? status : 'OPEN';
            const nextPage = queryPositiveInt(query.current_page) || 1;
            this.urlProposalId = queryPositiveInt(query.proposal);
            if (force || nextStatus !== this.statusFilter) {
                this.statusFilter = nextStatus;
                this.currentPage = nextPage;
                this.fetchFeedback();
            } else if (nextPage !== this.currentPage) {
                this.currentPage = nextPage;
                // 뒤로가기로 지금 목록에 없는 페이지가 돌아오면 마지막 페이지로 당긴다(조회 중·실패 땐 범위를 모르니 둔다).
                if (!this.loading && !this.loadError) this.clampPage();
            }
        },
        // 목록이 줄었으면 마지막 페이지로 당기고 URL 을 맞춘다(자동 보정이라 replace).
        clampPage() {
            const lastPage = Math.max(1, Math.ceil(this.feedback.length / MAX_SELECT));
            if (this.currentPage > lastPage) this.currentPage = lastPage;
            this.syncQuery('replace');
        },
        // 기본값(작업함·1페이지)과 빈 값은 생략한다. 다른 키(tab 등)는 그대로 둔다.
        syncQuery(method) {
            const query = {...this.$route.query};
            QUERY_KEYS.forEach((k) => { delete query[k]; });
            if (this.statusFilter !== 'OPEN') query.status = this.statusFilter;
            if (this.currentPage > 1) query.current_page = String(this.currentPage);
            if (this.urlProposalId) query.proposal = String(this.urlProposalId);
            if (!sameQuery(query, this.$route.query)) this.$router[method]({query}).catch(() => {});
        },
        // 사용자 조작(필터·페이지)은 push, 조회 후 페이지 보정·모달 열고 닫기는 replace.
        onStatusChange() {
            this.currentPage = 1;
            this.syncQuery('push');
            this.fetchFeedback();
        },
        onPageChange(page) {
            this.currentPage = page;
            this.syncQuery('push');
        },
        async fetchFeedback() {
            const req = ++this.fetchReq;  // 필터를 연달아 바꿨을 때 늦게 온 이전 응답은 버린다
            this.loading = true;
            this.loadError = false;
            try {
                let rows;
                let truncated;
                if (this.statusFilter === 'OPEN') {
                    // 작업함: 진행 중 3상태를 각각 조회해 id 로 병합(상태별 500 상한 안에서 안전).
                    const lists = await Promise.all(
                        WORKING_STATUSES.map((s) => this.fetchByStatus(s)));
                    truncated = lists.some((l) => l.length >= MAX_ROWS);
                    const byId = new Map();
                    lists.flat().forEach((f) => byId.set(f.id, f));
                    rows = [...byId.values()].sort((a, b) => b.id - a.id);
                } else {
                    rows = await this.fetchByStatus(this.statusFilter);
                    truncated = rows.length >= MAX_ROWS;
                }
                if (req !== this.fetchReq) return;
                this.truncated = truncated;
                this.feedback = rows;
                // 기각·반영 뒤 다시 불러와도 보던 페이지에 머문다. 줄어든 만큼만 마지막 페이지로 당긴다.
                this.clampPage();
                // 목록이 바뀌면 사라진 행의 선택은 버린다.
                const ids = new Set(this.feedback.map((f) => f.id));
                this.selectedIds = this.selectedIds.filter((id) => ids.has(id));
            } catch (e) {
                if (req !== this.fetchReq) return;
                // 지난 목록으로 반영·기각하지 않도록 비우고 '없음' 대신 오류를 보인다.
                // 페이지는 범위를 모르므로 그대로 두어 [다시 시도] 가 같은 위치를 연다.
                this.feedback = [];
                this.selectedIds = [];
                this.truncated = false;
                this.loadError = true;
                if (e?.response?.status === 401) return;
                this.$bvToast.toast(getErrorMessage(e, '피드백 목록을 불러오지 못했습니다.'), {
                    title: '오류', variant: 'danger',
                });
            } finally {
                if (req === this.fetchReq) this.loading = false;
            }
        },
        async fetchByStatus(status) {
            const {data} = await this.$axios.get('/supplement-prompt/feedback', {
                params: {status},
            });
            return (data && data.feedback) || [];
        },
        // 열린 개선 제안에 든 피드백은 서버가 IN_PROGRESS/READY 로 내려주지만,
        // 경쟁 상태 방어로 open proposal 검사도 함께 건다.
        hasOpenProposal(item) {
            return !!(item.proposal && item.proposal.status === 'OPEN');
        },
        isSelectable(item) {
            return item.status === 'PENDING' && !this.hasOpenProposal(item);
        },
        isCheckDisabled(item) {
            if (!this.isSelectable(item)) return true;
            if (this.selectedIds.includes(item.id)) return false;  // 해제는 항상 가능
            return this.selectedIds.length >= MAX_SELECT;
        },
        terminalCount(proposal) {
            return (proposal.targets || []).filter(
                (t) => ACCEPTED_TARGET_TERMINALS.includes(t.status)).length;
        },
        // 반영 칸 title — 한 줄로 줄인 대상별 진행 · 적용 버전(펼침과 같은 내용).
        progressTitle(item) {
            const lines = [this.progressLabel(item)];
            const targets = (item.proposal && item.proposal.targets) || [];
            if (targets.length) {
                lines.push(`진행 ${this.terminalCount(item.proposal)}/${targets.length}`);
                targets.forEach((t) => lines.push(`${previewTargetLabel(t.asset_type)}: ${targetStatusLabel(t.status)}`));
            } else {
                this.appliedList(item).forEach((a) => lines.push(`${previewTargetLabel(a.asset_type)} v${a.version}`));
            }
            return lines.join('\n');
        },
        toggleOne(id) {
            const idx = this.selectedIds.indexOf(id);
            if (idx >= 0) {
                this.selectedIds.splice(idx, 1);
                return;
            }
            if (this.selectedIds.length >= MAX_SELECT) {
                this.$bvToast.toast(
                    `한 번에 ${MAX_SELECT}건까지 반영할 수 있습니다. 나눠서 진행해 주세요.`,
                    {title: '선택 상한', variant: 'warning'});
                return;
            }
            this.selectedIds.push(id);
        },
        toggleAll() {
            if (this.allPageChecked || this.selectedIds.length) {
                this.selectedIds = [];
                return;
            }
            const room = MAX_SELECT - this.selectedIds.length;
            const picked = this.pageSelectableIds.slice(0, room);
            this.selectedIds = [...this.selectedIds, ...picked];
            if (picked.length < this.pageSelectableIds.length) {
                this.$bvToast.toast(
                    `한 번에 ${MAX_SELECT}건까지 반영할 수 있어 ${picked.length}건만 추가했습니다.`,
                    {title: '선택 상한', variant: 'info'});
            }
        },
        // 신규 반영 — 선택한 피드백으로 새 개선 제안을 만든다. proposal id 는 생성 뒤 URL 에 남긴다.
        openPreview() {
            if (!this.selectedIds.length) return;
            this.modalProposal = null;
            this.modalProposalId = null;
            this.previewOpen = true;
        },
        // 특정 피드백 행의 진행 중 개선 제안을 이어서 본다.
        continueProposal(item) {
            if (!this.hasOpenProposal(item)) return;
            this.modalProposal = item.proposal;
            this.modalProposalId = null;
            this.previewOpen = true;
            this.setUrlProposal(item.proposal.id);
        },
        // 이어보기 배너 — 링크(?proposal=) 또는 이 브라우저(localStorage)의 proposal id 로 재수화한다.
        resumePreview() {
            const id = this.bannerProposalId;
            if (!id) return;
            this.modalProposal = null;
            this.modalProposalId = id;
            this.previewOpen = true;
            this.setUrlProposal(id);
        },
        // 열린 개선 제안을 URL 에 남긴다(링크로 공유·새로고침 후 이어보기). 모달 열고 닫기라 replace.
        setUrlProposal(id) {
            this.urlProposalId = typeof id === 'number' ? id : null;
            this.syncQuery('replace');
        },
        // 반영된 자산 표시 — 새 applied_assets(다중) 우선, 없으면 레거시 단일 폴백.
        appliedList(item) {
            if (Array.isArray(item.applied_assets) && item.applied_assets.length) {
                return item.applied_assets;
            }
            if (item.applied_asset_type) {
                return [{asset_type: item.applied_asset_type, version: item.applied_version}];
            }
            return [];
        },
        // 배너의 제안을 지운다. 링크로 받은 제안은 URL 에서, 같은 id 가 이 브라우저 기록에도 있으면 그것도 지운다
        // (다른 id 의 이 브라우저 기록은 남겨 다음 배너로 보인다).
        dismissPendingProposal() {
            if (this.bannerProposalId === this.pendingProposalId) {
                clearActiveProposal();
                this.pendingProposalId = null;
            }
            if (this.urlProposalId) this.setUrlProposal(null);
        },
        onProposalCreated({proposalId} = {}) {
            // 새 개선 제안이 만들어지면 선택은 소진된다(그 피드백들은 이제 진행 중).
            this.selectedIds = [];
            this.pendingProposalId = loadActiveProposal();
            this.setUrlProposal(proposalId);
        },
        onProposalCompleted() {
            this.pendingProposalId = loadActiveProposal();
        },
        onModalClose() {
            this.previewOpen = false;
            this.modalProposal = null;
            this.modalProposalId = null;
            this.setUrlProposal(null);
            // 모달이 store 를 갱신했을 수 있으니 배너 상태를 다시 읽고 목록을 새로고침한다.
            this.pendingProposalId = loadActiveProposal();
            this.fetchFeedback();
        },
        async dismissSelected() {
            const ids = [...this.selectedIds];
            if (this.dismissing) return;
            const ok = await this.$showModal({
                type: 'confirm',
                state: 'warning',
                title: '기각 확인',
                message: `선택한 ${ids.length}건을 기각합니다. 인박스 대기 목록에서 제외되며, 기각한 피드백은 [기각] 필터에서 대기로 되돌릴 수 있습니다.`,
            });
            if (!ok) return;

            this.dismissing = true;
            this.dismissDone = 0;
            this.dismissTotal = ids.length;
            let failed = 0;
            for (const id of ids) {
                try {
                    // 순차 처리 — 부분 실패해도 성공분은 그대로 반영된다.
                    // eslint-disable-next-line no-await-in-loop
                    await this.$axios.patch(`/supplement-prompt/feedback/${id}`, {status: 'DISMISSED'});
                } catch (e) {
                    failed += 1;
                }
                this.dismissDone += 1;
            }
            this.dismissing = false;
            if (failed) {
                this.$bvToast.toast(`${ids.length}건 중 ${failed}건 기각에 실패했습니다.`, {
                    title: '일부 실패', variant: 'warning',
                });
            } else {
                this.$bvToast.toast(`${ids.length}건을 기각했습니다.`, {title: '완료', variant: 'success'});
            }
            this.selectedIds = [];
            await this.fetchFeedback();
        },
        async restoreDismissed(item) {
            if (this.restoringId) return;
            this.restoringId = item.id;
            try {
                await this.$axios.patch(`/supplement-prompt/feedback/${item.id}`, {status: 'PENDING'});
                this.$bvToast.toast('반영 대기로 되돌렸습니다.', {title: '완료', variant: 'success'});
                await this.fetchFeedback();
            } catch (e) {
                if (e?.response?.status === 401) return;
                this.$bvToast.toast(getErrorMessage(e, '되돌리지 못했습니다.'), {title: '오류', variant: 'danger'});
            } finally {
                this.restoringId = null;
            }
        },
        openManualModal() {
            this.manualForm = {content: '', sub_code: '', campaign_id: '', company_id: ''};
            this.manualSubmitted = false;
            this.manualError = '';
            this.$bvModal.show('manual-feedback-modal');
        },
        // 모달은 등록에 성공했을 때만 닫는다 — 실패하면 입력을 남기고 사유를 창 안에 보인다.
        async createManualFeedback(evt) {
            if (evt) evt.preventDefault();
            if (this.manualSubmitting) return;
            this.manualSubmitted = true;
            this.manualError = '';
            const content = (this.manualForm.content || '').trim();
            if (!content) return;
            const toInt = (v) => (v === '' || v === null ? null : Number(v));
            this.manualSubmitting = true;
            try {
                await this.$axios.post('/supplement-prompt/feedback', {
                    content,
                    sub_code: this.manualForm.sub_code || null,
                    campaign_id: toInt(this.manualForm.campaign_id),
                    company_id: toInt(this.manualForm.company_id),
                });
                this.$bvModal.hide('manual-feedback-modal');
                this.$bvToast.toast('피드백을 등록했습니다.', {title: '완료', variant: 'success'});
                this.fetchFeedback();
            } catch (e) {
                if (e?.response?.status === 401) return;
                this.manualError = getErrorMessage(e, '피드백 등록에 실패했습니다.');
            } finally {
                this.manualSubmitting = false;
            }
        },
        sourceVariant(type) {
            return SOURCE_TYPE_VARIANTS[type] || 'secondary';
        },
    },
};
</script>

<style scoped>
/* 고정 열 합 748(선택 40 · 출처 116 · 판정 200 · 반영 128 · 등록 140 · 동작 124) + 문항 · 대상과 내용 칸 ~142씩.
   1366 화면 · 좌측 메뉴 펼침의 탭 본문 폭(약 1034 = 1076 - 카드 테두리 2 - 탭 여백 40) 안 — 더 좁으면 가로 스크롤 */
.feedback-inbox ::v-deep .feedback-inbox-table {
    min-width: 1032px;
}

/* 펼침의 내용 전문 — 발송 문구 수정 피드백은 "[변경 전]/[변경 후]" 두 문단이라 줄바꿈을 살린다. */
.feedback-content {
    white-space: pre-wrap;
    word-break: break-word;
}
</style>
